import { RequiredMark } from "@/ui";
import { ArchivedRecord, useArchived } from "@/ui/ArchivedRecord";
import { requisitionOpen } from "@/api/tools";
import { ObjectReadGuard } from "@/ui/ObjectReadGuard";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  accessApproveRequest,
  accessDecline,
  accessExtend,
  accessGrant,
  accessList,
  accessRequest,
  accessRevoke,
  delegationApproveProposal,
  delegationGrant,
  delegationList,
  delegationPropose,
  delegationRevoke,
  type DelegationListOutput,
} from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { businessLocalToISO, isoToBusinessLocal } from "@/api/runtime";
import { roleLabel, Banner, Dialog, Empty, Field, Pill, ReasonDialog, Receipt, Skeleton, Table, fmtDate } from "@/ui";

// V1-INTERACTIONS §4.19 — Recruiter scope (Law 8): the owner sees requests and grants, chooses read or cover, extends
// and revokes; another Recruiter asks for access. §4.20 — stand-ins on this requisition's seats, named by the seat
// holder (or Admin), proposed by the Recruiter, approved by the seat holder, revoked by either.

const SCOPE_WORDS: Record<string, string> = { read: "Read", cover: "Cover" };
const SCOPE_HELP: Record<string, string> = {
  read: "Every read the owner has — no writes.",
  cover: "The owner's writes as well, except granting access, closing, reopening, reassigning a role, naming a stand-in and exporting the Summary Sheet.",
};

const SEATS: [string, string][] = [
  ["named_hrbp", "HR Business Partner on this requisition"],
  ["hiring_manager", "Hiring Manager on this requisition"],
];

// POLICY §5's four, in the words of V1-INTERACTIONS §4.20.
export const CAPABILITIES: { key: "attend" | "intake" | "approve" | "decide"; label: string; help: string }[] = [
  { key: "attend", label: "Attend", help: "the intake or debrief for me" },
  { key: "intake", label: "Intake", help: "edit Parts 1 and 2, comment, raise change requests" },
  { key: "approve", label: "Approve", help: "finalize the intake, approve the packet set and kickoff, acknowledge flags, approve reopen / openings / out-of-range compensation, decide the recruitment request" },
  { key: "decide", label: "Decide", help: "resume, screen and advance decisions, holds, early dispositions" },
];

/** UAT F-12: a seat with neither the access list nor a stand-in to read or name meets this, never an empty page. */
export const NOT_FOR_YOUR_ROLE = "This page is not available to your role.";

const STATUS_TONE: Record<string, "ok" | "warn" | "bad" | "info" | "muted"> = { proposed: "warn", active: "ok", revoked: "muted", expired: "muted", declined: "muted" };
const STATUS_WORDS: Record<string, string> = { proposed: "proposed — waiting for the seat holder", active: "active", revoked: "revoked", expired: "ended", declined: "handed back" };

// A day picked here is a BUSINESS day: 08 October means 08 October where the calendar lives, not where the reader's
// laptop happens to be, so both ends go through the business-zone helpers (Codex #9 M13; UAT-02 finding 25).
function isoAtFive(day: string): string {
  // a stand-in's last day ends with the working day; the server closes it at the calendar's close
  return businessLocalToISO(`${day}T17:00`);
}
function isoDayStart(day: string): string {
  // the first day starts when the day does, so a stand-in named this morning can act this morning
  return businessLocalToISO(`${day}T00:00`);
}
function dayAfter(days: number): string {
  // the day as the BUSINESS calendar reads it: after eight in the evening in New York the reader's own clock is already
  // on tomorrow in UTC, and the picker would open on a day the server does not call today
  return isoToBusinessLocal(new Date(Date.now() + days * 86_400_000).toISOString()).slice(0, 10);
}

export function AccessPage({ id, askNow }: { id: string; askNow: boolean }) {
  const q = useRead(["requisition", id], () => requisitionOpen({ requisition_id: id }));
  return <ObjectReadGuard query={q} allowed={!!q.data && "can" in q.data}><ArchivedRecord record={q.data}><AccessContent id={id} askNow={askNow && q.data?.state_archive !== "archived"} /></ArchivedRecord></ObjectReadGuard>;
}

export function AccessContent({ id, askNow }: { id: string; askNow: boolean }) {
  const { me, can } = useSession();
  const { register } = useViewState();
  useEffect(() => register({ view: "access", ids: { requisition: id }, summary: `access on ${id}` }), [register, id]);
  const list = useRead(["access", id], () => accessList({ requisition_id: id }), { enabled: can("access.list"), retry: false });
  const inval = [["access", id], ["delegations", id], ["delegations"], ["requisition", id], ["requisitions"], ["today"]] as const;
  const ask = useWrite((i: Parameters<typeof accessRequest>[0], k) => accessRequest(i, k), inval);
  const approve = useWrite((i: Parameters<typeof accessApproveRequest>[0], k) => accessApproveRequest(i, k), inval);
  const decline = useWrite((i: { requisition_id: string; access_request_id: string; reason: string }, k) => accessDecline(i, k), inval);
  const grant = useWrite((i: Parameters<typeof accessGrant>[0], k) => accessGrant(i, k), inval);
  const extend = useWrite((i: Parameters<typeof accessExtend>[0], k) => accessExtend(i, k), inval);
  const revoke = useWrite((i: { requisition_id: string; grant_id: string; reason: string }, k) => accessRevoke(i, k), inval);
  const [dlg, setDlg] = useState<{ kind: "ask" } | { kind: "decline"; id: string } | { kind: "revoke"; id: string } | { kind: "grant" } | { kind: "approve"; id: string } | { kind: "extend"; id: string; scope: string } | null>(askNow ? { kind: "ask" } : null);
  const [grantee, setGrantee] = useState("");
  const [days, setDays] = useState(7);
  const [scope, setScope] = useState<"read" | "cover">("cover");
  const [askScope, setAskScope] = useState<"read" | "cover">("read");
  const mine = (list.data?.mine ?? {}) as { is_owner?: boolean; scope?: string | null; until?: string | null };
  const archived = useArchived();
  const isOwner = !archived && !!mine.is_owner;
  const notOwnerRecruiter = !archived && !!me?.roles.includes("recruiter") && (list.isError || (list.data && !mine.is_owner));
  const known = useMemo(() => {
    const out = new Map<string, string>();
    for (const g of list.data?.grants ?? []) {
      out.set(g.grantee_id, g.grantee_name);
      out.set(g.granted_by_id, g.granted_by_name);
    }
    for (const a of list.data?.requests ?? []) out.set(a.requester_id, a.requester_name);
    return out;
  }, [list.data]);

  return (
    <div>
      <div className="hrow">
        <div>
          <h1 className="page">Access &amp; stand-ins · {id}</h1>
          <p className="lede"><a href={`#requisitions/${id}`}>← Requisition</a> · {(can("access.request") || can("access.list")) && <>Recruiters see every requisition and who owns it, open their own, and ask for time-boxed access to the rest. </>}A stand-in covers a named seat for a while, under their own name.</p>
        </div>
        <div className="acts">
          {isOwner && (
            <button className="btn btn--primary" onClick={() => { setScope("cover"); setDlg({ kind: "grant" }); }}>
              Grant access
            </button>
          )}
        </div>
      </div>
      {mine.scope && mine.until && (
        <div className="callout callout--warn" role="status">
          You are working under a grant: <b>{SCOPE_WORDS[mine.scope] ?? mine.scope}</b> on {id} until {fmtDate(mine.until)}. {SCOPE_HELP[mine.scope]}
        </div>
      )}
      {notOwnerRecruiter && (
        <section className="card">
          <h2 className="sec">Request access</h2>
          <p className="muted">The owner or Admin grants up to 30 days (default 7); the grant is recorded and re-checked whenever queued work runs.</p>
          <Banner error={ask.error} />
          {ask.isSuccess ? (
            <Receipt>Requested — the owner has it on Today.</Receipt>
          ) : (
            <button className="btn btn--primary" onClick={() => setDlg({ kind: "ask" })}>
              Ask for access
            </button>
          )}
        </section>
      )}
      {list.isLoading && can("access.list") && <Skeleton />}
      {list.data && (
        <>
          <section>
            <h2 className="sec">
              Requests <span className="sec__n">{list.data.requests.length}</span>
            </h2>
            {list.data.requests.length === 0 ? (
              <Empty heading="Access requests are clear">No access requests.</Empty>
            ) : (
              <Table
                label="Access requests"
                head={
                  <tr>
                    <th>Who</th>
                    <th>Asked for</th>
                    <th>Reason</th>
                    <th>Asked</th>
                    <th>Status</th>
                    <th className="sticky-r">Action</th>
                  </tr>
                }
              >
                {list.data.requests.map((a) => (
                  <tr key={a.id}>
                    <td>{a.requester_name}</td>
                    {/* POLICY §4.1: what was ASKED for, which is not what the owner ends up granting (Codex #9 N2) */}
                    <td>
                      <Pill tone={a.scope === "cover" ? "info" : "muted"} title={SCOPE_HELP[a.scope] ?? a.scope_label}>
                        {SCOPE_WORDS[a.scope] ?? a.scope_label}
                      </Pill>
                      {a.granted_scope && a.granted_scope !== a.scope && <div className="muted">granted {SCOPE_WORDS[a.granted_scope] ?? a.granted_scope}</div>}
                    </td>
                    <td>{a.reason}</td>
                    <td>{fmtDate(a.requested_at, true)}</td>
                    <td>
                      <Pill tone={a.status === "requested" ? "warn" : a.status === "granted" ? "ok" : "muted"}>{a.status}</Pill>
                      {a.decided_by_name && <span className="muted"> · {a.decided_by_name}</span>}
                    </td>
                    <td className="sticky-r">
                      {a.status === "requested" && isOwner && (
                        <div className="row" style={{ gap: 6 }}>
                          <button className="btn btn--sm btn--primary" onClick={() => { setScope("cover"); setDays(7); setDlg({ kind: "approve", id: a.id }); }}>
                            Grant
                          </button>
                          <button className="btn btn--sm" onClick={() => setDlg({ kind: "decline", id: a.id })}>
                            Decline
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </section>
          <section>
            <h2 className="sec">
              Grants <span className="sec__n">{list.data.grants.length}</span>
            </h2>
            {list.data.grants.length === 0 ? (
              <Empty heading="No access grants yet" action={isOwner ? <button className="btn btn--primary" onClick={() => { setScope("cover"); setDlg({ kind: "grant" }); }}>Grant access</button> : undefined}>No grants on this requisition.</Empty>
            ) : (
              <Table
                label="Access grants"
                head={
                  <tr>
                    <th>Grantee</th>
                    <th>Scope</th>
                    <th>Granted by</th>
                    <th>Reason</th>
                    <th>Window</th>
                    <th>State</th>
                    <th className="sticky-r">Action</th>
                  </tr>
                }
              >
                {list.data.grants.map((g) => (
                  <tr key={g.id}>
                    <td>{g.grantee_name}<div className="muted mono">{g.grantee_id}</div></td>
                    <td>
                      <Pill category tone="muted" title={SCOPE_HELP[g.scope] ?? g.scope_label}>
                        {SCOPE_WORDS[g.scope] ?? g.scope_label}
                      </Pill>
                    </td>
                    <td>{g.granted_by_name}</td>
                    <td className="wrap">{g.reason}</td>
                    <td>
                      {fmtDate(g.granted_at)} → {fmtDate(g.expires_at)}
                    </td>
                    <td>
                      {g.revoked_at ? <Pill tone="muted">revoked</Pill> : g.live ? <Pill tone="ok">live</Pill> : <Pill tone="muted">expired</Pill>}
                      {g.superseded_by_id && <div className="muted">replaced by a later grant</div>}
                      {g.supersedes_id && <div className="muted">extends an earlier grant</div>}
                      {/* a grant replaced by an extension already says so in words; its bookkeeping reason is not for the reader */}
                      {g.revoked_at && g.revoke_reason && !g.superseded_by_id && <div className="muted wrap">{g.revoke_reason}</div>}
                      {g.screens_opened > 0 && <div className="muted">{g.screens_opened} screen{g.screens_opened === 1 ? "" : "s"} opened</div>}
                    </td>
                    <td className="sticky-r">
                      {g.live && isOwner && (
                        <div className="row" style={{ gap: 6 }}>
                          <button className="btn btn--sm" onClick={() => { setScope(g.scope === "read" ? "read" : "cover"); setDays(7); setDlg({ kind: "extend", id: g.id, scope: g.scope }); }}>
                            Extend
                          </button>
                          <button className="btn btn--sm" onClick={() => setDlg({ kind: "revoke", id: g.id })}>
                            Revoke
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </section>
          <Banner error={approve.error ?? decline.error ?? revoke.error ?? grant.error ?? extend.error} />
        </>
      )}
      <Delegation rid={id} known={known} fallback={can("access.list") ? null : <Empty heading="">{NOT_FOR_YOUR_ROLE}</Empty>} />
      <ReasonDialog title="Ask for access" label="Why you need it" confirm="Ask" open={dlg?.kind === "ask"} onClose={() => setDlg(null)} busy={ask.isPending} error={ask.error} onConfirm={(reason) => void ask.mutateAsync({ requisition_id: id, reason, scope: askScope }).then(() => setDlg(null))}>
        <ScopeChoice value={askScope} onChange={setAskScope} />
        <p className="muted">The owner decides what to give you; what you asked for is shown beside your request.</p>
      </ReasonDialog>
      <ReasonDialog title="Decline the request" confirm="Decline" open={dlg?.kind === "decline"} onClose={() => setDlg(null)} busy={decline.isPending} error={decline.error} onConfirm={(reason) => dlg?.kind === "decline" && void decline.mutateAsync({ requisition_id: id, access_request_id: dlg.id, reason }).then(() => setDlg(null))} />
      <ReasonDialog title="Revoke the grant" confirm="Revoke" open={dlg?.kind === "revoke"} onClose={() => setDlg(null)} busy={revoke.isPending} error={revoke.error} onConfirm={(reason) => dlg?.kind === "revoke" && void revoke.mutateAsync({ requisition_id: id, grant_id: dlg.id, reason }).then(() => setDlg(null))} />
      <ReasonDialog title="Grant access" confirm="Grant" open={dlg?.kind === "grant"} onClose={() => setDlg(null)} busy={grant.isPending} error={grant.error} onConfirm={(reason) => void grant.mutateAsync({ requisition_id: id, grantee_id: grantee.trim(), scope, days, reason }).then(() => setDlg(null))}>
        <Field label="Recruiter" required id="g-who" hint="Who is covering for you — enter their person identifier.">
          <input id="g-who" list="access-people" value={grantee} onChange={(e) => setGrantee(e.target.value)} placeholder="p_recruiter_2" />
        </Field>
        <ScopeChoice value={scope} onChange={setScope} />
        <Field label="Days (1–30)" required id="g-days">
          <input id="g-days" type="number" min={1} max={30} value={days} onChange={(e) => setDays(Number(e.target.value) || 7)} />
        </Field>
      </ReasonDialog>
      <ReasonDialog title="Grant the access asked for" confirm="Grant" optional open={dlg?.kind === "approve"} onClose={() => setDlg(null)} busy={approve.isPending} error={approve.error} onConfirm={(reason) => dlg?.kind === "approve" && void approve.mutateAsync({ requisition_id: id, access_request_id: dlg.id, scope, days, ...(reason ? { reason } : {}) }).then(() => setDlg(null))}>
        <ScopeChoice value={scope} onChange={setScope} />
        <Field label="Days (1–30)" required id="ap-days">
          <input id="ap-days" type="number" min={1} max={30} value={days} onChange={(e) => setDays(Number(e.target.value) || 7)} />
        </Field>
      </ReasonDialog>
      <ReasonDialog title="Extend the grant" confirm="Extend" open={dlg?.kind === "extend"} onClose={() => setDlg(null)} busy={extend.isPending} error={extend.error} onConfirm={(reason) => dlg?.kind === "extend" && void extend.mutateAsync({ requisition_id: id, grant_id: dlg.id, scope, days, reason }).then(() => setDlg(null))}>
        <p className="muted">A grant is never edited: this writes a new grant that replaces the old one, up to 30 days from today.</p>
        <ScopeChoice value={scope} onChange={setScope} />
        <Field label="Days (1–30)" required id="ex-days">
          <input id="ex-days" type="number" min={1} max={30} value={days} onChange={(e) => setDays(Number(e.target.value) || 7)} />
        </Field>
      </ReasonDialog>
      <datalist id="access-people">
        {Array.from(known.entries()).map(([pid, name]) => (
          <option key={pid} value={pid}>
            {name}
          </option>
        ))}
      </datalist>
    </div>
  );
}

function ScopeChoice({ value, onChange }: { value: "read" | "cover"; onChange: (v: "read" | "cover") => void }) {
  return (
    <fieldset className="radios radios--stack">
      <legend>
        Scope <RequiredMark />
      </legend>
      {(["read", "cover"] as const).map((s) => (
        <label key={s} className={`opt opt--block${value === s ? " opt--on" : ""}`}>
          <input type="radio" name="grant-scope" value={s} checked={value === s} onChange={() => onChange(s)} />
          <span>
            <b>{SCOPE_WORDS[s]}</b>
            <span className="opt__help">{SCOPE_HELP[s]}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

// ---- §4.20 stand-ins -------------------------------------------------------------------------------------------------

type Row = DelegationListOutput["delegations"][number];

function Delegation({ rid, known, fallback }: { rid: string; known: Map<string, string>; fallback: ReactNode }) {
  const { me, can } = useSession();
  const q = useRead(["delegations", rid], () => delegationList({ requisition_id: rid }), { retry: false, enabled: can("delegation.list") });
  const inval = [["delegations", rid], ["delegations"], ["today"], ["intake", rid]] as const;
  const grant = useWrite((i: Parameters<typeof delegationGrant>[0], k) => delegationGrant(i, k), inval);
  const propose = useWrite((i: Parameters<typeof delegationPropose>[0], k) => delegationPropose(i, k), inval);
  const approve = useWrite((i: { delegation_id: string }, k) => delegationApproveProposal(i, k), inval);
  const revoke = useWrite((i: { delegation_id: string; reason: string }, k) => delegationRevoke(i, k), inval);
  const [dlg, setDlg] = useState<null | { kind: "name" } | { kind: "propose" } | { kind: "revoke"; id: string; withdraw: boolean }>(null);
  const archived = useArchived();
  const canName = !archived && can("delegation.grant");
  const canPropose = !archived && can("delegation.propose");
  const rows = q.data?.delegations ?? [];
  const pid = me?.person_id ?? "";
  const isAdmin = !!me?.roles.includes("admin");
  const withdrawing = dlg?.kind === "revoke" && dlg.withdraw;
  /** POLICY §5: the person whose seat it is, the stand-in and Admin end a stand-in; the Recruiter who suggested one may
   *  take their own suggestion back, and only while it is still a suggestion. `delegation.revoke` is in every one of
   *  those actors' manifests, so the row — not the manifest — says which of them this reader is. */
  function endLabel(d: Row): "Revoke" | "Withdraw" | null {
    if (archived || !can("delegation.revoke") || (d.status !== "proposed" && d.status !== "active")) return null;
    if (isAdmin || (pid !== "" && (pid === d.grantor_id || pid === d.grantee_id))) return "Revoke";
    if (d.status === "proposed" && pid !== "" && pid === d.proposed_by_id) return "Withdraw";
    return null;
  }
  // a seat that can only read them waits for the read; refused, or never theirs, the page's fallback stands here (F-12)
  if (!canName && !canPropose && !q.data) return q.isLoading ? <Skeleton rows={2} /> : <>{fallback}</>;
  return (
    <section>
      <div className="hrow hrow--sec">
        <h2 className="sec">
          Stand-ins <span className="sec__n">{rows.length}</span>
        </h2>
        <div className="acts">
          {canName && (
            <button className="btn btn--primary" onClick={() => setDlg({ kind: "name" })}>
              Name a stand-in
            </button>
          )}
          {canPropose && (
            <button className="btn" onClick={() => setDlg({ kind: "propose" })}>
              Propose a stand-in
            </button>
          )}
        </div>
      </div>
      <p className="muted">A stand-in covers one seat for a fixed period and only for what is ticked. They act under their own name, every act says who it was for, and the seat holder&apos;s own action always wins.</p>
      {/* a refusal from a dialog belongs in that dialog; only the row actions report here */}
      <Banner error={approve.error ?? revoke.error} />
      {/* F-46: "No stand-in" only once the read came back empty — never beside its loading line, never for a failed read */}
      {q.isLoading ? (
        <Skeleton rows={2} />
      ) : !q.data && q.isError ? (
        <Banner error={q.error} onRetry={() => void q.refetch()} />
      ) : rows.length === 0 ? (
        <Empty heading="No stand-in appointed" action={canName ? <button className="btn btn--primary" onClick={() => setDlg({ kind: "name" })}>Name a stand-in</button> : canPropose ? <button className="btn" onClick={() => setDlg({ kind: "propose" })}>Propose a stand-in</button> : undefined}>No stand-in on this requisition.</Empty>
      ) : (
        <Table
          label="Stand-ins"
          mobile="stack"
          head={
            <tr>
              <th>Stand-in</th>
              <th>Seat</th>
              <th>For</th>
              <th>May</th>
              <th>Window</th>
              <th>State</th>
              <th className="sticky-r">Action</th>
            </tr>
          }
        >
          {rows.map((d: Row) => (
            <tr key={d.id}>
              <td>{d.grantee_name}</td>
              <td>{d.seat_label}</td>
              <td>
                {d.grantor_name}
                {d.proposed_by_name && <div className="muted">proposed by {d.proposed_by_name}</div>}
              </td>
              {/* the delegation's own capability labels, the words #admin/delegation shows (F-46) */}
              <td className="wrap">{d.capability_labels.length ? <div className="acts">{d.capability_labels.map((label, i) => <Pill category tone="muted" key={label} title={CAPABILITIES.find((capability) => capability.key === d.capabilities[i])?.help ?? label}>{label}</Pill>)}</div> : <span className="muted">none ticked</span>}</td>
              <td>
                {fmtDate(d.valid_from)} → {fmtDate(d.valid_to)}
              </td>
              <td>
                <Pill tone={STATUS_TONE[d.status] ?? "muted"}>{STATUS_WORDS[d.status] ?? d.status}</Pill>
                {d.live && d.status === "active" ? <div className="muted">in force now</div> : null}
              </td>
              <td className="sticky-r">
                <div className="row" style={{ gap: 6 }}>
                  {!archived && d.status === "proposed" && can("delegation.approve_proposal") && (
                    <button className="btn btn--sm btn--primary" disabled={approve.isPending} onClick={() => void approve.mutateAsync({ delegation_id: d.id })}>
                      Approve
                    </button>
                  )}
                  {endLabel(d) !== null && (
                    <button className="btn btn--sm" onClick={() => setDlg({ kind: "revoke", id: d.id, withdraw: endLabel(d) === "Withdraw" })}>
                      {endLabel(d)}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
      <StandInDialog
        title={dlg?.kind === "propose" ? "Propose a stand-in" : "Name a stand-in"}
        confirm={dlg?.kind === "propose" ? "Propose" : "Name the stand-in"}
        reasonRequired={dlg?.kind === "propose"}
        open={dlg?.kind === "name" || dlg?.kind === "propose"}
        onClose={() => setDlg(null)}
        busy={grant.isPending || propose.isPending}
        error={grant.error ?? propose.error}
        known={() => known}
        directory={q.data?.directory}
        onSubmit={(v) =>
          dlg?.kind === "propose"
            ? void propose.mutateAsync({ requisition_id: rid, seat: v.seat, grantee_id: v.grantee, capabilities: v.capabilities, valid_from: isoDayStart(v.from), valid_to: isoAtFive(v.to), reason: v.reason }).then(() => setDlg(null))
            : void grant.mutateAsync({ requisition_id: rid, seat: v.seat, grantee_id: v.grantee, capabilities: v.capabilities, valid_from: isoDayStart(v.from), valid_to: isoAtFive(v.to), ...(v.reason ? { reason: v.reason } : {}) }).then(() => setDlg(null))
        }
      />
      <ReasonDialog title={withdrawing ? "Withdraw the suggestion" : "End the stand-in"} confirm={withdrawing ? "Withdraw" : "Revoke"} open={dlg?.kind === "revoke"} onClose={() => setDlg(null)} busy={revoke.isPending} error={revoke.error} onConfirm={(reason) => dlg?.kind === "revoke" && void revoke.mutateAsync({ delegation_id: dlg.id, reason }).then(() => setDlg(null))}>
        <p className="muted">{withdrawing ? "It never took effect, so nothing has been recorded under it." : "Anything already recorded stays exactly as it was."}</p>
      </ReasonDialog>
    </section>
  );
}

interface StandInValues {
  seat: "hiring_manager" | "named_hrbp";
  grantee: string;
  capabilities: ("attend" | "intake" | "approve" | "decide")[];
  from: string;
  to: string;
  reason: string;
}

export function StandInDialog({ title, confirm, open, onClose, onSubmit, busy, error, known, reasonRequired, fixedSeat, directory }: { title: string; confirm: string; open: boolean; onClose: () => void; onSubmit: (v: StandInValues) => void; busy: boolean; error: Parameters<typeof Banner>[0]["error"]; known: (seat: "hiring_manager" | "named_hrbp") => Map<string, string>; reasonRequired?: boolean | undefined; fixedSeat?: "hiring_manager" | "named_hrbp" | undefined; directory?: { person_id: string; name: string; roles: string[]; exclusions: Record<string, string | null> }[] | undefined }) {
  const [seat, setSeat] = useState<"hiring_manager" | "named_hrbp">(fixedSeat ?? "named_hrbp");
  const [grantee, setGrantee] = useState("");
  const [caps, setCaps] = useState<Record<string, boolean>>({ attend: true });
  const [from, setFrom] = useState(() => dayAfter(0));
  const [to, setTo] = useState(() => dayAfter(14));
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!open) return;
    setSeat(fixedSeat ?? "named_hrbp");
    setGrantee("");
    setCaps({ attend: true });
    setFrom(dayAfter(0));
    setTo(dayAfter(14));
    setReason("");
  }, [open, fixedSeat]);
  const chosen = CAPABILITIES.filter((c) => caps[c.key]).map((c) => c.key);
  const ready = grantee.trim().length > 0 && (!directory || directory.some(person => person.person_id === grantee && person.exclusions[seat] === null)) && chosen.length > 0 && !!to && (!reasonRequired || reason.trim().length >= 3);
  return (
    <Dialog title={title} open={open} onClose={onClose}>
      {/* the form is long: a refusal goes at the top, where it is read without scrolling back */}
      <Banner error={error} />
      {!fixedSeat && (
        <Field label="Seat" required id="si-seat">
          <select id="si-seat" value={seat} onChange={(e) => { setSeat(e.target.value as "named_hrbp"); setGrantee(""); }}>
            {SEATS.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Stand-in" required id="si-who" hint="They must already hold that role, and may not be the Recruiter, the other named seat or a panel seat here.">
        {directory ? <select id="si-who" value={grantee} onChange={e => setGrantee(e.target.value)}>
          <option value="">Choose a stand-in…</option>
          {directory.map(person => <option key={person.person_id} value={person.person_id} disabled={person.exclusions[seat] !== null}>{person.name} · {person.roles.map(roleLabel).join(", ")} · {person.person_id}{person.exclusions[seat] ? ` — ${person.exclusions[seat]}` : ""}</option>)}
        </select> : <input id="si-who" list="standin-people" value={grantee} onChange={(e) => setGrantee(e.target.value)} placeholder="Person ID" />}
      </Field>
      {directory && <details><summary>Why someone cannot be selected</summary><ul>{directory.filter(person => person.exclusions[seat]).map(person => <li key={person.person_id}>{person.name} — {person.exclusions[seat]}</li>)}</ul></details>}
      <fieldset className="radios radios--stack">
        <legend>
          What they may do <RequiredMark />
        </legend>
        {CAPABILITIES.map((c) => (
          <label key={c.key} className={`opt opt--block${caps[c.key] ? " opt--on" : ""}`}>
            <input type="checkbox" checked={!!caps[c.key]} onChange={(e) => setCaps({ ...caps, [c.key]: e.target.checked })} />
            <span>
              <b>{c.label}</b>
              <span className="opt__help">{c.help}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <div className="grid--dates">
        <Field label="From" required id="si-from">
          <input id="si-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="To" required id="si-to" hint="At most 60 days.">
          <input id="si-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>
      <Field label="Reason" required={reasonRequired} id="si-why">
        <textarea id="si-why" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
      </Field>
      {!ready && <p id="standin-blocker" className="field__hint">Select an eligible stand-in, their authority and an end date, and complete the required reason.</p>}
      <div className="row row--end" style={{ gap: 8, marginTop: 8 }}>
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" aria-disabled={busy || !ready} aria-describedby={!ready ? "standin-blocker" : undefined} disabled={busy || !ready} onClick={() => onSubmit({ seat, grantee: grantee.trim(), capabilities: chosen, from, to, reason: reason.trim() })}>
          {busy ? "Saving…" : confirm}
        </button>
      </div>
      {/* only people the seat itself can take: whoever the server already names as a stand-in on THIS seat here */}
      <datalist id="standin-people">
        {Array.from(known(seat).entries()).map(([pid, name]) => (
          <option key={pid} value={pid}>
            {name}
          </option>
        ))}
      </datalist>
    </Dialog>
  );
}
